import React, { useCallback, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Slide,
  Snackbar,
  Toolbar,
  Typography,
  CircularProgress,
} from '@mui/material';
import Cookies from 'universal-cookie';
// Icons
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SettingsIcon from '@mui/icons-material/Settings';
//Custom components
import Navigation from '../../Components/Navigation/navigation';
import DataContext from '../../Contexts/DataContext';
import { useAuth } from '../../Contexts/AuthContext';
import ProjectSelector from '../../Components/ProjectSelector/projectSelector';
import SimulationBanner from '../../Components/SimulationBanner/SimulationBanner';
import Change from './Change/change';
import GLKeyManagement from '../GLKeyManagement/glKeyManagement';
import ProjectSettingsModal from '../../Components/ProjectSettingsModal';
import ProjectHomeAgGrid from './projectHomeAgGrid';
import GridLoadingOverlay from '../../Components/GridLoadingOverlay/GridLoadingOverlay';
import GridLoadingOrchestrator from '../../Helpers/gridLoadingOrchestrator';
import useCookie from '../../Helpers/cookies';
import { getAccountsByGlKeys } from '../../Helpers/dataApi';
import { exportProjectToExcel } from '../../Helpers/excelExport';
import { fireOnboardingStep } from '../../Helpers/helpApi';
import { logError } from '../../Helpers/errorLogger';
import SupportContactMessage from '../../Components/SupportContactMessage';
import AddProForma from './AddProForma';
import AgGridErrorBoundary from '../../Components/AgGridErrorBoundary/AgGridErrorBoundary';


export default function ProjectHome(props) {
  const auth = useAuth();
  const activeUser = auth.activeUser;
  const context = useContext(DataContext);
  const history = useHistory();

  const [projectIsLoading, setProjectIsLoading] = useState(false);
  const [activeProjectId] = useCookie('activeProjectId', false);
  const activeProjectInfo = useMemo(() => context.activeProjectInfo, [context.activeProjectInfo])
  const lastLoadedProjectIdRef = useRef(null); // Track last loaded project to prevent duplicate loads

  // Redirect to dashboard if no active project
  // Three states for activeProjectInfo:
  // - null: initial/loading state (don't redirect)
  // - false: explicitly cleared (redirect)
  // - object: project loaded (don't redirect)
  useEffect(() => {
    // Debug logging to track redirect triggers
    if (process.env.NODE_ENV !== 'production') {
      console.log('ProjectHome: Redirect check', {
        activeProjectId,
        activeProjectInfo,
        activeProjectInfoType: typeof activeProjectInfo,
        willRedirect: !activeProjectId || activeProjectId === false || activeProjectInfo === false,
      });
    }
    
    // Redirect if no cookie OR if activeProjectInfo is explicitly false (cleared)
    if (!activeProjectId || activeProjectId === false || activeProjectInfo === false) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('ProjectHome: Redirecting to dashboard');
      }
      history?.push('/dashboard');
    }
    // Don't redirect if activeProjectInfo is null (loading) or an object (loaded)
  }, [activeProjectId, activeProjectInfo, history]);

  // Initialize loading orchestrator
  const [loadingOrchestrator] = useState(() => new GridLoadingOrchestrator());
  const [loadingState, setLoadingState] = useState({
    isLoading: false,
    progress: 0,
    currentOperation: null,
    operations: {},
    failedOperations: []
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isAddingProForma, setIsAddingProForma] = useState(false);
  const [isManagingGLKeys, setIsManagingGLKeys] = useState(false);
  const [projectSettingsOpen, setProjectSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState(0);
  const [newChange, setNewChange] = useState({});
  const [changeToEdit, setChangeToEdit] = useState(false);
  const [isProcessingChange, setIsProcessingChange] = useState(false); // Track change operations

  // Label of a stat-driver change that has been identified as needing a loading
  // state but whose API call hasn't completed yet. Set before previewChange is
  // awaited so the grid can start showing the column spinner immediately.
  // Cleared by the grid once changesVersion fires, or on failure.
  const [pendingCalculatingLabel, setPendingCalculatingLabel] = useState(null);

  const handleCalculatingStart = useCallback((changeLabel) => {
    setPendingCalculatingLabel(changeLabel);
  }, []);

  const handleCalculatingFailed = useCallback(() => {
    setPendingCalculatingLabel(null);
  }, []);

  const [refreshTable, setRefreshTable] = useState(false);
  const [gridRefreshKey, setGridRefreshKey] = useState(0); // Force grid remount after changes

  const [exportButtonEnabled, setExportButtonEnabled] = useState(false);
  const [exportFileGenerated, setExportFileGenerated] = useState(false);
  const [exportFileLoading, setExportFileLoading] = useState(false);



  const [success, setSuccess] = useState({
    success: false,
    message: '',
  });

  const [error, setError] = useState({
    error: false,
    message: '',
  });

  // Loading error state with retry capability
  const [loadingError, setLoadingError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  // Tracks whether the user has dismissed the partial-load failure banner.
  // Resets to false whenever the project changes (new load attempt).
  const [loadFailureDismissed, setLoadFailureDismissed] = useState(false);
  const MAX_RETRIES = 2;

  // Compute if data is fully loaded and ready for interaction
  const isDataFullyLoaded = useMemo(() => {
    return !loadingState.isLoading && 
           loadingState.progress === 100 &&
           context.activeProjectAttributes?.entities?.length > 0 &&
           context.activeProjectAttributes?.departments?.length > 0 &&
           context.activeProjectAttributes?.accountsMetadata?.length > 0;
  }, [loadingState.isLoading, loadingState.progress, 
      context.activeProjectAttributes?.entities?.length,
      context.activeProjectAttributes?.departments?.length,
      context.activeProjectAttributes?.accountsMetadata?.length]);

  // Subscribe to loading orchestrator state changes
  useEffect(() => {
    const unsubscribe = loadingOrchestrator.subscribe(setLoadingState);
    return unsubscribe;
  }, [loadingOrchestrator]);

  // Consolidated data loading with orchestrator
  useEffect(() => {
    let isMounted = true;
    
    async function loadAllProjectData() {
      // Skip if no project ID
      if (!activeProjectId) {
        return;
      }
      
      // Skip if this project was already loaded (prevent duplicate loads)
      if (lastLoadedProjectIdRef.current === activeProjectId && !loadingError) {
        return;
      }

      lastLoadedProjectIdRef.current = activeProjectId;

      // Clear any previous loading errors
      setLoadingError(null);
      setLoadFailureDismissed(false);

      // Reset orchestrator for new load
      loadingOrchestrator.reset();

      // Register all operations upfront (including grid operations)
      loadingOrchestrator.registerOperation('project-info', 1);
      loadingOrchestrator.registerOperation('entities', 2);
      loadingOrchestrator.registerOperation('departments', 3);
      loadingOrchestrator.registerOperation('accounts', 4);
      loadingOrchestrator.registerOperation('changes', 6);
      loadingOrchestrator.registerOperation('grid-setup', 7);
      // Note: master-stats will be loaded by grid after initial render

      try {
        // Load project info with timeout
        loadingOrchestrator.startOperation('project-info', 30000);
        await context.setActiveProject(activeProjectId, activeUser.accessToken);
        if (!isMounted) return;
        loadingOrchestrator.completeOperation('project-info');

        // Load entities with timeout
        loadingOrchestrator.startOperation('entities', 30000);
        const entitiesResult = await context.setActiveProjectEntitiesAttribute(
          activeProjectId, 
          activeUser.accessToken
        );
        if (!isMounted) return;
        const entities = entitiesResult || context.activeProjectAttributes.entities || [];
        loadingOrchestrator.completeOperation('entities');

        // Load Departments with timeout
        loadingOrchestrator.startOperation('departments', 30000);
        
        const departmentsResult = await context.setActiveProjectDepartmentsAttribute(
          activeProjectId,
          entities,
          activeUser.accessToken
        );
        
        if (!isMounted) return;
        loadingOrchestrator.completeOperation('departments');

        const departments = departmentsResult || context.activeProjectAttributes.departments || [];

        // Load accounts with timeout. Always fetch regardless of department count so
        // accountsMetadata is never left as null (which signals "not yet loaded").
        // For projects with no departments the API returns [] and the field is set to
        // an empty array, correctly indicating "loaded but empty".
        loadingOrchestrator.startOperation('accounts', 45000); // Longer timeout for accounts
        
        await context.setActiveProjectAccountsAttribute(
          activeProjectId,
          activeUser.accessToken
        );
        
        if (!isMounted) return;
        loadingOrchestrator.completeOperation('accounts');

      } catch (error) {
        console.error('[ProjectHome] Error loading project data:', error);
        
        // Log error to CloudWatch for monitoring
        logError(error, {
          severity: error.isTimeout ? 'Warning' : 'Error',
          userId: activeUser?.id,
          metadata: {
            projectId: activeProjectId,
            retryCount: retryCount,
            failedOperations: loadingOrchestrator.getFailedOperations().map(op => op.name),
            isTimeout: error.isTimeout || false,
          }
        });
        
        // Reset the last loaded ID on error so retry is possible
        lastLoadedProjectIdRef.current = null;
        
        // Mark any in-progress operations as failed
        const operations = loadingOrchestrator.getAllOperations();
        operations.forEach(op => {
          if (op.status === 'loading' || op.status === 'pending') {
            loadingOrchestrator.failOperation(op.name, error);
          }
        });

        // Set user-friendly error message
        let errorMessage = 'Failed to load project data. ';
        
        if (error.message?.includes('timed out')) {
          errorMessage = 'The project is taking too long to load. This may be due to a large dataset or network issues. ';
        } else if (error.message?.includes('not found') || error.message?.includes('404')) {
          errorMessage = 'This project could not be found or is no longer accessible. ';
        } else if (error.message?.includes('Access denied') || error.message?.includes('403')) {
          errorMessage = 'You do not have permission to access this project. ';
        } else if (error.message?.includes('Network Error')) {
          errorMessage = 'Unable to connect to the server. Please check your internet connection. ';
        }

        setLoadingError({
          message: errorMessage,
          canRetry: retryCount < MAX_RETRIES && 
            !error.message?.includes('403') && !error.message?.includes('404') &&
            !error.message?.includes('Access denied') && !error.message?.includes('not found'),
          error: error
        });
      }
    }

    loadAllProjectData();

    return () => {
      isMounted = false;
    };
  }, [activeProjectId, retryCount]);

  /**
   * Toggle editing to show changeDetails screen or projectHomeAgGrid
   * @param {Object} changeData Existing change data
   */
  const handleStartEdit = (changeData) => {
    if (changeData) {
      setChangeToEdit(changeData);
      // Pro forma changes have function_operator 'account' + is_stat_driver true.
      // Route them to the AddProForma wizard; all other changes use the regular form.
      if (changeData.function_operator === 'account' && changeData.is_stat_driver) {
        setIsAddingProForma(true);
      } else {
        setIsEditing(true);
      }
    }
    else {
      console.error('No change data found');
    }
  }

  /**
   * Cancel a change or edit, clearing the form, and resetting the changeToEdit
   */
  const handleCancel = () => {
    setChangeToEdit(false);
    setIsEditing(false);
  }

  /**
   * Retry loading the project after an error
   */
  const handleRetryLoad = () => {
    setRetryCount(prev => prev + 1);
    setLoadingError(null);
    setLoadFailureDismissed(false);
  }

  /**
   * Go back to dashboard after a fatal error
   */
  const handleBackToDashboard = () => {
    history.push('/dashboard');
  }

  /**
   * Trigger an API request in DataProvider, pass new data to the projectHomeAgGrid component
   * @param {Object} change New change data
   */
  const addChange = async (change) => {
    setIsProcessingChange(true); // Show loading overlay
    let newChangeData;
    // Tracks whether setIsProcessingChange(false) was already called during
    // early navigation, so the finally block doesn't double-clear it.
    let processingCleared = false;

    // Stat driver and master stat changes navigate back to the grid immediately
    // after the API call confirms the record exists, then continue polling in the
    // background while the column loading state is visible. Regular changes use
    // the original synchronous flow (poll → navigate → remount grid).
    const isLongRunningChange = change.affects_stat_drivers || change.is_master_stat_change;
    
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[ProjectHome] Submitting change:', change.change_label);
      }
      newChangeData = await context.saveAccountChange(change, activeUser.id, activeProjectInfo.id, activeUser.accessToken);
      
      if (newChangeData) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[ProjectHome] Change submitted, polling for calculated values...');
        }
        
        // Poll for calculated values with exponential backoff
        // Large pro forma submissions (116 accounts × 12 months = 1,392 data points) need time to calculate
        const maxAttempts = 25;
        let attempt = 0;
        let changeHasValues = false;
        let latestChangeData = null;
        let navigatedBackEarly = false;
        
        // Use exponential backoff: start fast, then slow down
        // Attempts 1-5: 500ms (total: 2.5s)
        // Attempts 6-10: 1000ms (total: 5s additional = 7.5s cumulative)
        // Attempts 11+: 2000ms (total: 30s additional = 37.5s cumulative)
        const getPollingDelay = (attemptNum) => {
          if (attemptNum <= 5) return 500;
          if (attemptNum <= 10) return 1000;
          return 2000;
        };
        
        // Note: if the user navigates away from ProjectHome while background polling
        // is running, the await chain will continue but DataProvider's setState is
        // guarded by this._isMounted, so no state updates hit an unmounted component.
        while (attempt < maxAttempts && !changeHasValues) {
          attempt++;
          const delay = getPollingDelay(attempt);
          if (process.env.NODE_ENV !== 'production') {
            console.log(`[ProjectHome] Polling attempt ${attempt}/${maxAttempts} (waiting ${delay}ms)...`);
          }
          
          // Wait before polling
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Fetch latest changes from backend — this also updates context so the
          // grid's changesVersion fires and starts rendering the new column.
          const changes = await context.setActiveProjectChangesAttribute(activeProjectInfo.id, activeUser.accessToken);
          
          // Find the newly created change
          const createdChange = changes?.find(c => c.id === newChangeData.id);
          
          if (createdChange) {
            latestChangeData = createdChange;
            
            const isMasterStatChange = createdChange.is_master_stat_change;
            
            if (!isMasterStatChange) {
              // Standard or stat-driver change — dates are ready synchronously.
              changeHasValues = true;
            } else {
              // Master Stat change — poll until backend distribution calc completes.
              const dates = createdChange.dates;
              if (dates && typeof dates === 'object' && Object.keys(dates).length > 0) {
                const glKeys = Object.keys(dates);
                const sampleGlKeys = glKeys.slice(0, Math.min(3, glKeys.length));
                let hasPopulatedValues = false;
                
                for (const glKey of sampleGlKeys) {
                  const months = dates[glKey];
                  if (!months || typeof months !== 'object') continue;
                  
                  const sampleMonthKeys = Object.keys(months).slice(0, Math.min(4, Object.keys(months).length));
                  for (const monthKey of sampleMonthKeys) {
                    const monthEntry = months[monthKey];
                    const value = monthEntry && typeof monthEntry === 'object'
                      ? monthEntry.value : monthEntry;
                    if (value != null) { hasPopulatedValues = true; break; }
                  }
                  if (hasPopulatedValues) break;
                }
                if (hasPopulatedValues) changeHasValues = true;
              }
            }

            // For long-running changes: as soon as the change record exists in context
            // (first successful poll), navigate back to the grid so the user can see
            // the column loading state. Continue polling in the background for final values.
            //
            // setNewChange is intentionally not called here — the grid drives column
            // rendering via changesVersion (context.changes), not the newChange prop.
            // changesVersion fired on the setActiveProjectChangesAttribute call above,
            // so the column is already being added to the grid at this point.
            if (isLongRunningChange && !navigatedBackEarly) {
              navigatedBackEarly = true;
              context.clearLoadedParentChildren?.();
              setIsEditing(false);
              processingCleared = true;
              setIsProcessingChange(false);
              if (process.env.NODE_ENV !== 'production') {
                console.log('[ProjectHome] Navigated back early — continuing background poll for final values');
              }
            }
          } else if (process.env.NODE_ENV !== 'production') {
            console.log('[ProjectHome] Change not found in response yet...');
          }
        }
        
        if (!changeHasValues) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[ProjectHome] Timed out waiting for calculated values. Proceeding anyway.');
          }
          if (!latestChangeData) {
            const finalChanges = await context.setActiveProjectChangesAttribute(activeProjectInfo.id, activeUser.accessToken);
            latestChangeData = finalChanges?.find(c => c.id === newChangeData.id);
          }
        }
        
        if (process.env.NODE_ENV !== 'production') {
          console.log('[ProjectHome] Returning to grid view');
        }
        
        if (!navigatedBackEarly) {
          // Standard path: navigate back and remount grid now that data is ready.
          setIsEditing(false);
          setNewChange(latestChangeData || newChangeData);
          context.clearLoadedParentChildren?.();
          // Force grid to remount with fresh data by changing its key
          setGridRefreshKey(prev => prev + 1);
          // Give React time to propagate state changes through the component tree
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          // Long-running path: grid is already showing the column in loading state.
          // Do a final context update with the fully-populated change data so the
          // grid's changesVersion fires one more time with correct values, then let
          // the calculatingChangeIds clear itself via the 800ms minimum hold.
          if (latestChangeData) {
            await context.setActiveProjectChangesAttribute(activeProjectInfo.id, activeUser.accessToken);
          }
        }
        
        setSuccess({
          success: true,
          message: `Added change "${change.change_label}" successfully.`
        });
        
        return true;
      }
      else {
        // Stay on change screen on error
        setError({
          error: true,
          message: `Adding change "${change.change_label}" failed.`
        });
        
        return false;
      }
    } catch (error) {
      console.error('[ProjectHome] Error submitting change:', error);
      setError({
        error: true,
        message: `Failed to submit change: ${error.message || 'Unknown error'}`
      });
      return false;
    } finally {
      // Only clear if not already cleared during early navigation
      if (!processingCleared) setIsProcessingChange(false);
    }
  }

  /**
   * Trigger an API request to edit an existing change
   * @param {Object} change 
   */
  const editChange = async (change) => {
    setIsProcessingChange(true); // Show loading overlay
    
    try {
      const success = await context.editAccountChange(change, activeUser.id, activeProjectInfo.id, activeUser.accessToken);
      // console.log(success);
      
      if (success) {
        // Invalidate cache to force refetch of audit trail after edit
        if (context?.invalidateAuditTrailCache && change.id) {
          context.invalidateAuditTrailCache(change.id, 'change');
        }
        
        // Reload changes to get updated data
        await context.setActiveProjectChangesAttribute(activeProjectInfo.id, activeUser.accessToken);

        // Clear lazy-loaded parent children cache so rows re-fetch with updated change values
        context.clearLoadedParentChildren?.();

        // Force grid to remount with fresh data (same pattern as addChange)
        setGridRefreshKey(prev => prev + 1);

        // Wait for grid to process
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setNewChange(change);
        setSuccess({
          success: true,
          message: `Edited "${change.change_label}" successfully.`,
        });

        // Return to project home grid
        handleCancel();
        
        // Return success to indicate the edit was successful
        return true;
      }
      else {
        setError({
          error: true,
          message: `Editing "${change.change_label}" failed.`,
        });
        return false;
      }
    } finally {
      setIsProcessingChange(false); // Hide loading overlay
    }
  }

  /**
   * Trigger an API request to delete a change. Modify the Parent Change model if neccessary
   * @param {Object|number} changeOrId - Either a change object or a change ID
   * @param {Object} parent_change 
   */
  const deleteChange = async (changeOrId, parent_change) => {
    setIsProcessingChange(true); // Show loading overlay
    
    try {
      // Handle both change object and changeId
      const changeId = typeof changeOrId === 'object' ? changeOrId.id : changeOrId;
      const changeLabel = typeof changeOrId === 'object' ? changeOrId.change_label : 'Change';
      
      const success = await context.deleteAccountChange(changeId, parent_change, activeUser.accessToken);
      
      if (success) {
        setSuccess({
          success: true,
          message: `Deleted "${changeLabel}" successfully.`,
        });
      }
      else {
        setError({
          error: true,
          message: `Deleting "${changeLabel}" failed.`,
        });
      }

      // Return success so the grid layer knows whether to proceed with cleanup.
      return success;
    } finally {
      setIsProcessingChange(false); // Hide loading overlay
    }
  }

  const handleExportData = async () => {
    const accessToken = activeUser.accessToken;

    setExportFileLoading(true);
    try {
      // accountsMetadata is loaded from the metadata endpoint which omits the
      // dates JSONB blob for performance. We need full account data (with monthly
      // values) for the export, so fetch it now via bulk-gl-keys.
      const metadataAccounts = context.activeProjectAttributes?.accountsMetadata ?? [];
      const glKeys = metadataAccounts.map((a) => a.gl_key).filter(Boolean);

      let accountsWithDates = metadataAccounts; // fallback: metadata only (no dates)

      if (glKeys.length > 0) {
        // bulk-gl-keys caps at 5000 per request — batch if needed
        const BATCH_SIZE = 5000;
        const batches = [];
        for (let i = 0; i < glKeys.length; i += BATCH_SIZE) {
          batches.push(glKeys.slice(i, i + BATCH_SIZE));
        }

        const batchedAccounts = [];
        for (const batch of batches) {
          const resp = await getAccountsByGlKeys(activeProjectId, batch, accessToken);
          if (resp?.status === 200 && resp?.data?.accounts) {
            batchedAccounts.push(...resp.data.accounts);
          }
        }

        if (batchedAccounts.length > 0) {
          accountsWithDates = batchedAccounts;
        }
      }

      // Changes: already loaded in context — includes all change types
      const changes = context.activeProjectAttributes?.changes ?? [];

      exportProjectToExcel({
        accounts: accountsWithDates,
        changes,
        projectTitle: activeProjectInfo?.project_title ?? `project_${activeProjectId}`,
      });

      fireOnboardingStep('run-export', accessToken);
      setExportFileGenerated(true);
    } catch (err) {
      logError('handleExportData', err);
    } finally {
      setExportFileLoading(false);
    }
  };

  const formatDataToExport = (_changes) => {
    // No-op: export button state is driven by the dataLoaded callback below.
    // Previously populated exportProjectData for the old CSV export endpoint.
  };


  return (
    <Box
      sx={{
        display: 'flex'
      }}
    >
      <Navigation title={'Project Home'} />
      <Box
        component='main'
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflowX: 'hidden',

          position: 'relative',
        }}
      >
        <Toolbar />
        
        {/* Loading Error Display */}
        {loadingError && (
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {loadingError.canRetry && (
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={handleRetryLoad}
                      startIcon={<RotateRightIcon />}
                    >
                      Retry
                    </Button>
                  )}
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={handleBackToDashboard}
                  >
                    Back to Dashboard
                  </Button>
                </Box>
              }
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                Unable to Load Project
              </Typography>
              <SupportContactMessage 
                errorMessage={loadingError.message}
                errorDetails={{
                  stack: loadingError.error?.stack,
                  isTimeout: loadingError.error?.isTimeout,
                  statusCode: loadingError.error?.response?.status
                }}
                subject="Error loading project"
                context={{
                  projectId: activeProjectId,
                  retryCount: retryCount,
                  failedOperations: loadingState.failedOperations?.map(op => op.name).join(', ')
                }}
                showErrorDetails={true}
              />
              {loadingState.failedOperations?.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
                  Failed operations: {loadingState.failedOperations.map(op => op.name).join(', ')}
                </Typography>
              )}
            </Alert>
          </Container>
        )}

        {!projectIsLoading && activeProjectInfo && !loadingError &&
          <>
            <Box sx={{ px: 2, pt: 2 }}>
              <SimulationBanner />
            </Box>

            {/* Persistent banner for partial load failures (e.g. changes timeout).
                Shown after the loading overlay clears if any orchestrated operation
                failed. The grid is still usable but data may be incomplete. */}
            {!loadingState.isLoading && loadingState.failedOperations?.length > 0 && !loadFailureDismissed && (
              <Box sx={{ px: 2, pt: 1 }}>
                <Alert
                  severity="error"
                  onClose={() => {
                    // Dismiss by clearing the failed operations from orchestrator state.
                    // We do this via a local dismiss state rather than mutating the
                    // orchestrator so a page refresh resets correctly.
                    setLoadFailureDismissed(true);
                  }}
                  sx={{ mb: 0 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Some data failed to load — the grid may be incomplete.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {loadingState.failedOperations.some(op => op.error?.isTimeout)
                      ? 'A request took too long and was cancelled. This can happen with large projects on a slow connection.'
                      : 'One or more requests to the server returned an error.'}
                    {' '}Please refresh the page to try again.
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, mb: 0.5 }}>
                    Failed: {loadingState.failedOperations.map(op => op.name).join(', ')}
                  </Typography>
                  <SupportContactMessage
                    errorMessage={loadingState.failedOperations.map(op => op.error?.message || op.name).join('; ')}
                    errorDetails={{
                      isTimeout: loadingState.failedOperations.some(op => op.error?.isTimeout),
                    }}
                    subject="Project load error"
                    context={{
                      projectId: activeProjectId,
                      failedOperations: loadingState.failedOperations.map(op => op.name).join(', '),
                    }}
                    showErrorDetails={false}
                  />
                </Alert>
              </Box>
            )}
            <Grid
              item
              xs={12}
            >
              <Typography
                component='h2'
                variant='h6'
                color='inherit'
                sx={{
                  mb: '1rem',
                  mr: '55px',
                  mt: '1rem',
                  textAlign: 'center',
                }}
              >
                {activeProjectInfo.project_title}
              </Typography>
            </Grid>
            <Slide
              appear={false}
              direction={'right'}
              in={!isEditing && !isAddingProForma}
              sx={{
                position: 'absolute',
              }}
            >
              <Grid
                container
                item
                xs={12}
              >
                <Grid container item xs={12}
                  sx={{
                    mb: '1rem',
                    paddingLeft: '1rem',
                    gap: '1rem',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    color={'success'}
                    onClick={() => { setChangeToEdit(false); setIsEditing(true); }}
                    startIcon={<AddCircleIcon />}
                    variant='contained'
                    disabled={!isDataFullyLoaded}
                  >
                    Add Bridge Column
                  </Button>
                  <IconButton
                    color="default"
                    onClick={() => { setSettingsInitialTab(0); setProjectSettingsOpen(true); }}
                    sx={{ marginLeft: 'auto' }}
                    title="Project Settings & Actions"
                    disabled={!isDataFullyLoaded}
                  >
                    <SettingsIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <GridLoadingOverlay 
                      loadingState={isProcessingChange ? {
                        isLoading: true,
                        progress: 50,
                        currentOperation: 'processing-change',
                        operations: {},
                        failedOperations: []
                      } : loadingState} 
                    />
                    <AgGridErrorBoundary onReset={() => setGridRefreshKey(k => k + 1)}>
                    <ProjectHomeAgGrid
                      key={`grid-${activeProjectId}-${gridRefreshKey}`}
                      loadingOrchestrator={loadingOrchestrator}
                      deleteChange={deleteChange}
                      handleStartEdit={handleStartEdit}
                      newChange={newChange}
                      setNewChange={setNewChange}
                      isLoading={projectIsLoading}
                      refreshTable={refreshTable}
                      pendingCalculatingLabel={pendingCalculatingLabel}
                      onPendingCalculatingLabelConsumed={() => setPendingCalculatingLabel(null)}
                      changeDataToExport={(changes) => {
                        formatDataToExport(changes)
                      }}
                      dataLoaded = {
                        (dataLoaded) =>{
                          setExportButtonEnabled(dataLoaded)
                        }
                      }
                    />
                    </AgGridErrorBoundary>
                  </Box>
                </Grid>
              </Grid>
            </Slide>
            <Slide
              appear={false}
              direction='left'
              in={isEditing}
              mountOnEnter
              unmountOnExit
              sx={{
                position: 'absolute',
              }}
            >
              <Grid
                container
                item
                xs={12}
              >
                {!isManagingGLKeys &&
                  <Change
                    addChange={addChange}
                    cancelChange={handleCancel}
                    changeToEdit={changeToEdit}
                    editChange={editChange}
                    isEditing={isEditing}
                    onCalculatingStart={handleCalculatingStart}
                    onCalculatingFailed={handleCalculatingFailed}
                    projectTitle={activeProjectInfo.project_title}
                    toggleEditing={() => {setChangeToEdit(false); setIsEditing(!isEditing);}}
                  />
                }
                {isManagingGLKeys &&
                  <GLKeyManagement
                    onClose={() => {setIsEditing(false); setIsManagingGLKeys(false);}}
                  />
                }
              </Grid>
            </Slide>
            <Slide
              appear={false}
              direction='left'
              in={isAddingProForma && !changeToEdit}
              mountOnEnter
              unmountOnExit
              sx={{
                position: 'absolute',
              }}
            >
              <Grid
                container
                item
                xs={12}
              >
                <AddProForma
                  addChange={addChange}
                  cancelChange={() => { setIsAddingProForma(false); setChangeToEdit(false); }}
                  changeToEdit={changeToEdit}
                  editChange={editChange}
                  toggleEditing={() => { setIsAddingProForma(!isAddingProForma); setChangeToEdit(false); }}
                />
              </Grid>
            </Slide>

            {/* Edit mode: open inside a Dialog so the grid stays visible behind it */}
            <Dialog
              open={isAddingProForma && Boolean(changeToEdit)}
              onClose={() => { setIsAddingProForma(false); setChangeToEdit(false); }}
              maxWidth="lg"
              fullWidth
              PaperProps={{ sx: { borderRadius: 2 } }}
            >
              <DialogContent sx={{ p: 0 }}>
                <AddProForma
                  addChange={addChange}
                  cancelChange={() => { setIsAddingProForma(false); setChangeToEdit(false); }}
                  changeToEdit={changeToEdit}
                  editChange={editChange}
                  toggleEditing={() => { setIsAddingProForma(false); setChangeToEdit(false); }}
                />
              </DialogContent>
            </Dialog>
          </>
        }
      </Box>
      {projectIsLoading &&
        <Box
          sx={{
            backgroundColor: '#f5f5f5',
            height: '100%',
            position: 'absolute',
            width: '100%',
            zIndex: '2',
          }}
        >
          <RotateRightIcon
            sx={{
              animation: 'spin 2s linear infinite',
              '@keyframes spin': {
                '0%': {
                  transform: 'rotate(0deg)',
                },
                '100%': {
                  transform: 'rotate(360deg)',
                },
              },
              color: 'grey',
              fontSize: 50,
              left: 'calc(50% - 25px)',
              position: 'absolute',
              top: '30%',
            }}
          />
        </Box>
      }
      <Snackbar
        // anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={10000}
        onClose={() => { setSuccess({ success: false, message: '' }) }}
        open={success.success}>
        <Alert
          onClose={() => { setSuccess({ success: false, message: '' }) }}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {success.message}
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={10000}
        onClose={() => { setError({ error: false, message: '' }) }}
        open={error.error}
      >
        <Alert
          onClose={() => { setError({ error: false, message: '' }) }}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error.message}
        </Alert>
      </Snackbar>
      
      {/* Project Settings Modal */}
      <ProjectSettingsModal
        open={projectSettingsOpen}
        onClose={() => setProjectSettingsOpen(false)}
        isDataFullyLoaded={isDataFullyLoaded}
        initialTab={settingsInitialTab}
        exportButtonEnabled={exportButtonEnabled}
        exportFileLoading={exportFileLoading}
        onExportData={handleExportData}
        onOpenGLKeyManagement={() => {
          setProjectSettingsOpen(false);
          setIsEditing(true);
          setIsManagingGLKeys(true);
        }}
      />
    </Box>
  );
}